import { Button, Div, Flex, HtmlHeader, Inline, Link, minmax } from "@hopper-ui/components";
import { BarsIcon, DismissIcon } from "@hopper-ui/icons";
import { Breakpoints } from "@hopper-ui/styled-system";
import { ShareGateWordmarkLogo, useMinWidth } from "@sg-protect/components";
import type { ReactNode } from "react";
import { useShellTranslation } from "../../i18n/i18next.ts";

export interface HeaderProps {
    hamburger?: {
        onClick: () => void;
    };
    href?: string;
    userMenu?: ReactNode;
    workspaceMenu?: ReactNode;
    headerWidget?: ReactNode;
    commandPalette?: ReactNode;
    onClose?: () => void;
    accentColor?: string;
}

const ACCENT_HEIGHT = "3px";

export function Header(props: HeaderProps) {
    const {
        hamburger,
        href,
        userMenu = null,
        workspaceMenu = null,
        headerWidget = null,
        commandPalette = null,
        onClose,
        accentColor
    } = props;
    const { t } = useShellTranslation();
    const isDesktop = useMinWidth(Breakpoints.sm);

    return (
        <HtmlHeader
            display="grid"
            gridTemplateColumns={{
                base: ["1fr", "auto", "auto"],
                xs: [minmax("max-content", "1fr"), "auto", minmax("max-content", "1fr")]
            }}
            alignItems="center"
            gap="inline-sm"
            UNSAFE_paddingX="0.75rem"
            UNSAFE_height="4.5rem"
            borderBottom={accentColor ? undefined : "neutral-weak"}
            UNSAFE_borderBottom={accentColor ? `${ACCENT_HEIGHT} solid ${accentColor}` : undefined}
            backgroundColor="neutral"
        >
            <Div display="flex" alignItems="center" gap="inline-sm">
                {hamburger && !isDesktop && (
                    <Button aria-label={t("Header.menu")} onPress={hamburger.onClick} variant="ghost-secondary">
                        <BarsIcon />
                    </Button>
                )}

                {(!hamburger || isDesktop) && (
                    <Inline alignY="center">
                        <Link
                            data-telemetry="Header Logo"
                            href={href}
                            variant="secondary"
                            textDecoration="none"
                            isQuiet
                            isDisabled={!href}
                        >
                            <Div display="flex" alignItems="center" gap="inline-sm">
                                <ShareGateWordmarkLogo />
                            </Div>
                        </Link>
                    </Inline>
                )}
            </Div>
            <Flex minWidth={0}>{commandPalette}</Flex>
            <Flex alignItems="center" justifyContent="end" gap="inline-sm">
                {headerWidget}
                {workspaceMenu}
                {userMenu}
                {onClose && (
                    <Button aria-label={t("Header.close")} onPress={onClose} variant="ghost-secondary">
                        <DismissIcon />
                    </Button>
                )}
            </Flex>
        </HtmlHeader>
    );
}
